import { SegmentDataStructureType } from "@redotech/redo-customer-segments-common/marketing/segments/segment-data-structures";
import {
  CustomerCharacteristicType,
  CustomerDataSource,
} from "@redotech/redo-customer-segments-common/marketing/segments/segment-types";
import {
  TokenCompareOperators,
  tokenHierarchyPrerequisiteCounts,
} from "@redotech/redo-customer-segments-common/segments/segment-where-condition";
import { TokenHierarchyCompare } from "@redotech/redo-customer-segments-common/segments/segment-where-condition-zod-schema";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { filterTruthy } from "@redotech/util/array";
import { memo } from "react";
import {
  MultipleTokenOptionSelect,
  SingleTokenOptionSelect,
} from "./token-filter";

const tokenCompareOperatorToFriendly: Record<TokenCompareOperators, string> = {
  [TokenCompareOperators.ANY]: "is",
  [TokenCompareOperators.NONE]: "is not",
};

const tokenCompareOptions: RedoListItem<TokenCompareOperators>[] =
  Object.values(TokenCompareOperators).map((value) => ({
    id: value,
    text: tokenCompareOperatorToFriendly[value],
    value,
    type: "text",
  }));

export const TokenHierarchyFilter = memo(function TokenHierarchyFilter({
  comparison,
  setComparison,
  dimension,
  fieldSource,
  dataStructure,
  multiSelect = true,
  showOperatorDropdown = true,
}: {
  comparison: TokenHierarchyCompare;
  setComparison: (condition: TokenHierarchyCompare) => void;
  dimension: string;
  fieldSource?: CustomerDataSource;
  dataStructure: SegmentDataStructureType;
  multiSelect?: boolean;
  showOperatorDropdown?: boolean;
}) {
  const prerequisiteCount =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    tokenHierarchyPrerequisiteCounts[dimension as CustomerCharacteristicType];
  if (!prerequisiteCount) {
    throw new Error(`No prerequisite keys found for dimension ${dimension}`);
  }
  return (
    <>
      {showOperatorDropdown && (
        <RedoSingleSelectDropdownInput<TokenCompareOperators>
          fitToAnchor={false}
          flexGrow={false}
          options={tokenCompareOptions}
          optionSelected={({ item }) => {
            setComparison({ ...comparison, operator: item.value });
          }}
          selectedItem={tokenCompareOptions.find(
            (item) => item.id === comparison.operator,
          )}
          size="xs"
        />
      )}
      {Array.from({ length: prerequisiteCount }).map((_, index) => (
        <SingleTokenOptionSelect
          dataStructure={dataStructure}
          dimension={dimension}
          disabled={index > 0 && !comparison.prerequisiteValues[index - 1]}
          fieldSource={fieldSource}
          flexGrow={false}
          hierarchyIndex={index}
          key={index}
          precedingValues={comparison.prerequisiteValues.slice(0, index)}
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          selectedValue={comparison.prerequisiteValues[index]}
          setSelectedValue={(value) => {
            setComparison({
              ...comparison,
              prerequisiteValues: filterTruthy([
                ...comparison.prerequisiteValues.slice(0, index),
                value,
              ]),
              values: [],
            });
          }}
        />
      ))}
      {multiSelect ? (
        <MultipleTokenOptionSelect
          dataStructure={dataStructure}
          dimension={dimension}
          disabled={
            comparison.prerequisiteValues.length < prerequisiteCount ||
            !comparison.prerequisiteValues.every(Boolean)
          }
          fieldSource={fieldSource}
          hierarchyIndex={prerequisiteCount}
          key={dimension}
          precedingValues={comparison.prerequisiteValues}
          selectedValues={comparison.values}
          setSelectedValues={(values) => {
            setComparison({ ...comparison, values });
          }}
        />
      ) : (
        <SingleTokenOptionSelect
          dataStructure={dataStructure}
          dimension={dimension}
          disabled={
            comparison.prerequisiteValues.length < prerequisiteCount ||
            !comparison.prerequisiteValues.every(Boolean)
          }
          fieldSource={fieldSource}
          flexGrow={false}
          hierarchyIndex={prerequisiteCount}
          key={dimension}
          precedingValues={comparison.prerequisiteValues}
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          selectedValue={comparison.values[0]}
          setSelectedValue={(value) => {
            setComparison({ ...comparison, values: value ? [value] : [] });
          }}
        />
      )}
    </>
  );
});
